import React, { type FC, SyntheticEvent, useCallback, useMemo } from 'react';
import { type SxProps, type Theme, ToggleButton, ToggleButtonGroup, type ToggleButtonGroupProps } from '@mui/material';
import { mergeSx } from 'merge-sx';

import { IndicatorChipVariantTranslated } from '@/components/statuses/IndicatorChip';
import withFormControl from '@/hoc/withFormControl';

export interface ChipSelectProps {
  id?: string;
  name?: string;
  value?: string | number | null;
  onChange?: (newValue: string | number | null) => void;
  options?: IndicatorChipVariantTranslated[];
  sx?: SxProps<Theme>;
  color?: ToggleButtonGroupProps['color'];
  disabled?: boolean;
  enforcedValue?: boolean;
}

const ChipSelectField: FC<ChipSelectProps> = ({
  id,
  value,
  onChange,
  options = [],
  sx,
  color,
  disabled,
  enforcedValue = false
}) => {
  const fallbackValue = useMemo(() => {
    const fallbackOption = options?.[0]?.value;

    return fallbackOption && enforcedValue ? fallbackOption : '';
  }, [enforcedValue, options]);

  const handleChange = useCallback(
    (_event: SyntheticEvent, newValue: string | null) => {
      if (enforcedValue && !newValue && options?.length >= 3) {
        return;
      }
      let valueToAssign: string | number | null = newValue;

      if (enforcedValue && !newValue && options?.length === 2) {
        const alternativeValue = options.find(opt => opt.value !== value)?.value;
        valueToAssign = alternativeValue ?? null;
      }

      onChange?.(valueToAssign);
    },
    [onChange, value, options, enforcedValue]
  );

  return (
    <ToggleButtonGroup
      id={id}
      value={value || fallbackValue}
      sx={mergeSx<Theme>(
        {
          flexWrap: 'wrap',
          filter: disabled ? 'grayscale(100%)' : 'none',
          '& .MuiToggleButton-root': {
            gap: 0.5
          }
        },
        sx
      )}
      exclusive
      onChange={handleChange}
      color={color}
      disabled={disabled}
    >
      {(options || []).map(option => (
        <ToggleButton sx={option.sx} value={option.value || ''} key={option.value}>
          {option.Icon?.()}
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const ChipSelect = withFormControl<string | number | null, string | number | null, ChipSelectProps>(ChipSelectField);

export default ChipSelect;
